import { Box, Button, Illustration, Typography } from "@saviynt/design-system";
import { useState } from "react";
import { FormattedMessage } from "react-intl";

export interface MfeErrorStateProps {
  mfeName: string;
  error: Error | null;
  onRetry: () => void;
  onGoHome: () => void;
  /** When true, shows "failed to load" messaging instead of "runtime error" */
  isLoadError?: boolean;
}

export function MfeErrorState({
  mfeName,
  error,
  onRetry,
  onGoHome,
  isLoadError = false,
}: MfeErrorStateProps) {
  const isDev = import.meta.env.DEV;
  const [showDetails, setShowDetails] = useState(false);

  return (
    <Box
      className="flex items-center justify-center min-h-[400px] h-full w-full p-8"
      dataTestId="mfe-error-state"
    >
      {/* ─── Localized Error Card ───────────────────────────────────────── */}
      <Box
        className="max-w-[520px] w-full rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-secondary)] p-8 text-center shadow-lg"
        dataTestId="mfe-error-card"
      >
        {/* Icon */}
        <Box className="flex justify-center">
          <Illustration kind="Error" dataTestId="mfe-error-illustration" />
        </Box>

        {/* Title */}
        <section className="mt-5">
          <Typography kind="h2" dataTestId="mfe-error-title">
            {isLoadError ? (
              <FormattedMessage
                id="G5lX6J"
                defaultMessage="Unable to load {mfeName}"
                description="Title shown when an MFE fails to load"
                values={{ mfeName }}
              />
            ) : (
              <FormattedMessage
                id="RxsNhT"
                defaultMessage="Something went wrong"
                description="Title shown when an MFE crashes at runtime"
              />
            )}
          </Typography>
        </section>

        {/* Description — user-friendly, no technical jargon */}
        <section className="mt-6">
          <Typography
            kind="body1"
            className="text-[var(--color-foreground-secondary)]"
            dataTestId="mfe-error-description"
          >
            {isLoadError ? (
              <FormattedMessage
                id="DVpjNW"
                defaultMessage="We couldn't connect to this module. This might be due to a network interruption or temporary server maintenance. Please try again."
                description="User-friendly description when an MFE fails to load"
              />
            ) : (
              <FormattedMessage
                id="YNSuzJ"
                defaultMessage="This section encountered an unexpected error. Please try reloading or return to the home page."
                description="User-friendly description when an MFE crashes at runtime"
              />
            )}
          </Typography>
        </section>

        {/* Action Buttons */}
        <Box className="mt-6 flex items-center justify-center gap-3">
          <Button
            kind="filled"
            size="medium"
            onClick={onRetry}
            dataTestId="mfe-error-retry-button"
          >
            {isLoadError ? (
              <FormattedMessage
                id="cIn3Qr"
                defaultMessage="Reload Module"
                description="Reload button for failed MFE load"
              />
            ) : (
              <FormattedMessage
                id="dKQY5T"
                defaultMessage="Try Again"
                description="Retry button for crashed MFE"
              />
            )}
          </Button>
          <Button
            kind="outlined"
            size="medium"
            onClick={onGoHome}
            dataTestId="mfe-error-home-button"
          >
            <FormattedMessage
              id="WWTLzG"
              defaultMessage="Go to Home"
              description="Navigate to home page with full refresh to recover from MFE error"
            />
          </Button>
        </Box>

        {/* ─── Collapsible Technical Details ──────────────────────────── */}
        {error?.message && (
          <Box className="mt-6 border-t border-[var(--color-border-subtle)] pt-4">
            <Button
              kind="ghost"
              size="small"
              onClick={() => setShowDetails((v) => !v)}
              dataTestId="mfe-error-details-toggle"
            >
              {showDetails ? (
                <FormattedMessage
                  id="ZVv2LE"
                  defaultMessage="Hide technical details"
                  description="Toggle to collapse error details"
                />
              ) : (
                <FormattedMessage
                  id="BMtSxv"
                  defaultMessage="Show technical details"
                  description="Toggle to expand error details"
                />
              )}
            </Button>
            {showDetails && (
              <Box className="mt-3 p-3 bg-[var(--color-surface-secondary)] rounded-lg overflow-auto max-h-[200px] text-left">
                <Typography
                  kind="body3"
                  className="font-mono whitespace-pre-wrap text-[var(--color-foreground-secondary)] break-all"
                >
                  {error.message}
                  {isDev && error.stack && (
                    <>
                      {"\n\n"}
                      {error.stack}
                    </>
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default MfeErrorState;
